import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { SparkLineChart } from "@mui/x-charts/SparkLineChart";
import React from "react";
import {
  absenteesBySection,
  ATTENDANCE_PCT,
  feeByGrade,
  monthlyFeeCollection,
  overviewStats,
  TOTAL_ABSENT,
  TOTAL_PRESENT,
} from "./landingMockData";
// Same colours and axis styling as the admin dashboard charts.
import { CHART_COLORS, chartSx } from "../charts/chartTheme";
import { FEE_CHART_TITLE, FeeAnalyticsBarChart } from "../../features/dashboard/charts/FeeAnalyticsBarChart";

const lakhs = (v: number | null) => (v == null ? "" : `₹${v}L`);

// A light "app window" around each preview so it reads as a product screen, not a figure.
export const ChartFrame = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 3,
      border: "1px solid #e5e7eb",
      boxShadow: "0 18px 50px rgba(15, 23, 42, 0.08)",
      overflow: "hidden",
      bgcolor: "#fff",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, borderBottom: "1px solid #eef2f6", bgcolor: "#f8fafc" }}>
      <Stack direction="row" spacing={0.75} aria-hidden>
        {["#f87171", "#fbbf24", "#34d399"].map((c) => (
          <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: c }} />
        ))}
      </Stack>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155", flexGrow: 1, lineHeight: 1.35 }}>
        {title}
      </Typography>
      <Chip label="Sample data" size="small" sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: "#eef2f6", color: "#475569" }} />
    </Box>
    <Box sx={{ p: { xs: 2, sm: 2.5 } }}>{children}</Box>
  </Paper>
);

const LegendDot = ({ color, label, value }: { color: string; label: string; value: string }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: color }} />
    <Typography sx={{ fontSize: 13, color: "#475569" }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{value}</Typography>
  </Stack>
);

// ---------------- Hero: attendance donut + monthly fee collection ----------------
export const HeroDashboardPreview = () => (
  <ChartFrame title="Today at a glance">
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" }, gap: 3, alignItems: "center" }}>
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569", mb: 1 }}>Attendance today</Typography>
        <Box sx={{ position: "relative", height: 170 }}>
          <PieChart
            height={170}
            hideLegend
            margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
            series={[
              {
                innerRadius: 58,
                outerRadius: 80,
                paddingAngle: 2,
                cornerRadius: 4,
                data: [
                  { id: "present", value: TOTAL_PRESENT, label: "Present", color: CHART_COLORS.series1 },
                  { id: "absent", value: TOTAL_ABSENT, label: "Absent", color: CHART_COLORS.series2 },
                ],
              },
            ]}
          />
          <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{ATTENDANCE_PCT}%</Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>present</Typography>
          </Box>
        </Box>
        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
          <LegendDot color={CHART_COLORS.series1} label="Present" value={TOTAL_PRESENT.toLocaleString("en-IN")} />
          <LegendDot color={CHART_COLORS.series2} label="Absent" value={String(TOTAL_ABSENT)} />
        </Stack>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569", mb: 1 }}>Monthly fee collection</Typography>
        <LineChart
          height={230}
          hideLegend
          dataset={monthlyFeeCollection}
          xAxis={[{ scaleType: "point", dataKey: "month", disableTicks: true }]}
          yAxis={[{ width: 40, disableLine: true, disableTicks: true, valueFormatter: (v: number) => `₹${v}L` }]}
          series={[{ dataKey: "amount", label: "Collected", color: CHART_COLORS.series1, area: true, curve: "monotoneX", showMark: false, valueFormatter: lakhs }]}
          grid={{ horizontal: true }}
          margin={{ top: 10, right: 20, bottom: 0, left: 0 }}
          sx={{
            ...chartSx,
            "& .MuiLineElement-root": { strokeWidth: 2 },
            "& .MuiAreaElement-root": { fillOpacity: 0.12 },
          }}
        />
      </Box>
    </Box>
  </ChartFrame>
);

// ---------------- Complete School Overview: stat tiles with sparklines ----------------
export const OverviewStatTiles = () => (
  <ChartFrame title="School overview">
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
      {overviewStats.map((stat) => (
        <Box key={stat.label} sx={{ p: 2, borderRadius: 2, border: "1px solid #eef2f6", bgcolor: "#fbfcfe" }}>
          <Typography sx={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>{stat.label}</Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: "#0f172a", lineHeight: 1.2, mt: 0.5 }}>{stat.value}</Typography>
          <Box sx={{ mt: 1 }}>
            <SparkLineChart
              data={stat.trend}
              // Fit the axis to the data so small movements (e.g. 1,120 → 1,212) read as a trend, not a flat line.
              yAxis={{ min: Math.min(...stat.trend), max: Math.max(...stat.trend) }}
              height={40}
              area
              curve="monotoneX"
              color={CHART_COLORS.series1}
              showTooltip
              showHighlight
              sx={{ "& .MuiAreaElement-root": { fillOpacity: 0.12 }, "& .MuiLineElement-root": { strokeWidth: 2 } }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  </ChartFrame>
);

// ---------------- Attendance Insights: absentees by grade & section ----------------
export const AbsenteesBySectionChart = () => (
  <ChartFrame title="Absentees by grade & section">
    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{TOTAL_ABSENT}</Typography>
      <Typography sx={{ fontSize: 13, color: "#64748b" }}>students absent today</Typography>
    </Stack>
    <BarChart
      height={260}
      hideLegend
      dataset={absenteesBySection}
      xAxis={[{ scaleType: "band", dataKey: "section", disableTicks: true, categoryGapRatio: 0.45 }]}
      yAxis={[{ width: 30, disableLine: true, disableTicks: true, tickMinStep: 2 }]}
      series={[{ dataKey: "absent", label: "Absent", color: CHART_COLORS.series1 }]}
      borderRadius={4}
      grid={{ horizontal: true }}
      margin={{ top: 10, right: 6, bottom: 0, left: 0 }}
      sx={chartSx}
    />
  </ChartFrame>
);

// ---------------- Revenue & Outstanding: the dashboard's own fee chart, fed sample data ----------------
// The real FeeAnalyticsBarChart draws the chart (so Home and dashboard can never drift apart);
// it renders bare here so it sits in the same app-window frame as the other Home charts.
export const FeeCollectionByGradeChart = () => (
  <ChartFrame title={FEE_CHART_TITLE}>
    <FeeAnalyticsBarChart data={feeByGrade} bare />
  </ChartFrame>
);
